import React, { useState, useEffect, useContext, useMemo } from "react";
import { useApiClient } from "../utils/apiClient";
import { AuthContext } from "../contexts/AuthContext";
import { PlusIcon, TrashIcon, PencilIcon, XMarkIcon, DocumentIcon, FolderIcon, ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import Workflows from "./Workflows";

const formatBytes = (n) => {
    if (n == null) return "—";
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const splitRootAndFolders = (files) => {
    const root = [];
    const groups = {};
    for (const f of files) {
        const parts = f.path.split("/");
        if (parts.length === 1) {
            root.push(f);
        } else {
            const folder = parts.slice(0, -1).join("/");
            if (!groups[folder]) groups[folder] = [];
            groups[folder].push(f);
        }
    }
    return {
        root: root.sort((a, b) => a.path.localeCompare(b.path)),
        folders: Object.keys(groups).sort().map((folder) => ({ folder, files: groups[folder] })),
    };
};

const FileRow = ({ f, isManager, onOpen, onDelete }) => (
    <tr className="hover:bg-gray-50">
        <td
            className="px-6 py-3 whitespace-nowrap text-sm text-gray-900 flex items-center gap-2 cursor-pointer hover:text-blue-700"
            onClick={() => onOpen(f)}
        >
            <DocumentIcon className="h-4 w-4 text-gray-400" />
            {f.path.split("/").pop()}
        </td>
        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
            {formatBytes(f.size)}
        </td>
        <td className="px-6 py-3 whitespace-nowrap text-sm text-gray-500">
            {f.last_modified ? new Date(f.last_modified).toLocaleString() : ""}
        </td>
        <td className="px-6 py-3 whitespace-nowrap text-sm text-right">
            <div className="flex justify-end items-center gap-3">
                <button
                    onClick={() => onOpen(f)}
                    className="text-blue-600 hover:text-blue-900 font-medium flex items-center gap-1"
                >
                    <PencilIcon className="h-4 w-4" />
                    {isManager ? "Edit" : "View"}
                </button>
                {isManager && (
                    <button
                        onClick={() => onDelete(f)}
                        className="text-red-600 hover:text-red-900 font-medium flex items-center gap-1"
                    >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                    </button>
                )}
            </div>
        </td>
    </tr>
);

export default function AgentFiles() {
    const apiClient = useApiClient();
    const { user } = useContext(AuthContext);
    const isManager = user?.activeRole === "Manager";

    const initialTab = new URLSearchParams(window.location.search).get("tab") === "workflows" ? "workflows" : "files";
    const [tab, setTab] = useState(initialTab);
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [editor, setEditor] = useState(null); // { path, content, isNew }
    const [collapsedFolders, setCollapsedFolders] = useState(new Set());

    const toggleFolder = (folder) => {
        setCollapsedFolders((prev) => {
            const next = new Set(prev);
            if (next.has(folder)) next.delete(folder); else next.add(folder);
            return next;
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { fetchFiles(); }, []);
    useEffect(() => {
        if (!success) return;
        const t = setTimeout(() => setSuccess(""), 8000);
        return () => clearTimeout(t);
    }, [success]);

    async function fetchFiles() {
        setLoading(true);
        setError("");
        try {
            const resp = await apiClient.get("/agent-files");
            setFiles(resp.files || []);
        } catch (e) {
            setError("Failed to load agent files");
        } finally {
            setLoading(false);
        }
    }

    async function openFile(file) {
        setLoading(true);
        setError("");
        try {
            const resp = await apiClient.get(`/agent-files?path=${encodeURIComponent(file.path)}`);
            setEditor({ path: resp.file.path, content: resp.file.content || "", isNew: false, originalPath: resp.file.path });
        } catch (e) {
            setError(`Failed to load ${file.path}`);
        } finally {
            setLoading(false);
        }
    }

    function newFile() {
        setEditor({ path: "", content: "", isNew: true, originalPath: null });
    }

    async function saveFile() {
        if (!editor.path.trim()) {
            setError("Path is required");
            return;
        }
        setLoading(true);
        setError("");
        try {
            await apiClient.post("/agent-files", { path: editor.path.trim(), content: editor.content });
            await fetchFiles();
            setSuccess(`Saved ${editor.path.trim()}`);
            setEditor(null);
        } catch (e) {
            setError(e?.response?.data?.message || "Failed to save file");
        } finally {
            setLoading(false);
        }
    }

    async function deleteFile(file) {
        if (!window.confirm(`Delete ${file.path}? This cannot be undone.`)) return;
        setLoading(true);
        setError("");
        try {
            await apiClient.delete("/agent-files", { path: file.path });
            await fetchFiles();
            setSuccess(`Deleted ${file.path}`);
        } catch (e) {
            setError(`Failed to delete ${file.path}`);
        } finally {
            setLoading(false);
        }
    }

    const { root, folders } = useMemo(() => splitRootAndFolders(files), [files]);

    const tabs = (
        <div className="mb-6 flex gap-1 border-b">
            {[
                { id: "files", label: "Project Files" },
                { id: "workflows", label: "Workflows" },
            ].map((t) => (
                <button
                    key={t.id}
                    onClick={() => {
                        setTab(t.id);
                        const url = new URL(window.location.href);
                        if (t.id === "workflows") url.searchParams.set("tab", "workflows"); else url.searchParams.delete("tab");
                        window.history.replaceState({}, "", url);
                    }}
                    className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        tab === t.id ? "border-blue-600 text-blue-700" : "border-transparent text-gray-500 hover:text-gray-700"
                    }`}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );

    if (tab === "workflows") {
        return (
            <div className="p-6">
                {tabs}
                <Workflows embedded />
            </div>
        );
    }

    return (
        <div className="p-6">
            {tabs}
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Agent Project Files</h1>
                    <p className="text-sm text-gray-500 mt-1">
                        Files the scheduling agent (Claude / Codex / etc.) reads as project context.
                    </p>
                </div>
                {isManager && (
                    <button
                        onClick={newFile}
                        className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
                    >
                        <PlusIcon className="h-5 w-5" />
                        New File
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">{error}</div>
            )}
            {success && (
                <div className="mb-4 p-4 bg-green-100 border border-green-400 text-green-700 rounded flex justify-between items-center">
                    <span>{success}</span>
                    <button onClick={() => setSuccess("")} className="text-green-700 hover:text-green-900">
                        <XMarkIcon className="h-5 w-5" />
                    </button>
                </div>
            )}

            {loading && !editor ? (
                <div className="text-center py-8">
                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="mt-2 text-gray-600">Loading files...</p>
                </div>
            ) : (
                <div className="bg-white shadow rounded-lg overflow-hidden">
                    {root.length === 0 && folders.length === 0 ? (
                        <div className="px-6 py-8 text-center text-gray-500">
                            No agent files yet. {isManager && 'Click "New File" to add one.'}
                        </div>
                    ) : (
                        <>
                            {root.length > 0 && (
                                <table className="min-w-full divide-y divide-gray-200">
                                    <tbody className="bg-white divide-y divide-gray-100">
                                        {root.map((f) => (
                                            <FileRow key={f.path} f={f} isManager={isManager} onOpen={openFile} onDelete={deleteFile} />
                                        ))}
                                    </tbody>
                                </table>
                            )}
                            {folders.map(({ folder, files }) => {
                                const isCollapsed = collapsedFolders.has(folder);
                                return (
                                    <div key={folder} className="border-t">
                                        <button
                                            onClick={() => toggleFolder(folder)}
                                            className="w-full px-6 py-3 bg-gray-50 hover:bg-gray-100 flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer transition-colors"
                                        >
                                            {isCollapsed
                                                ? <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                                                : <ChevronDownIcon className="h-4 w-4 text-gray-500" />}
                                            <FolderIcon className="h-5 w-5 text-gray-400" />
                                            <span>{folder}</span>
                                            <span className="text-xs text-gray-500 font-normal">({files.length})</span>
                                        </button>
                                        {!isCollapsed && (
                                            <table className="min-w-full divide-y divide-gray-200">
                                                <tbody className="bg-white divide-y divide-gray-100">
                                                    {files.map((f) => (
                                                        <FileRow key={f.path} f={f} isManager={isManager} onOpen={openFile} onDelete={deleteFile} />
                                                    ))}
                                                </tbody>
                                            </table>
                                        )}
                                    </div>
                                );
                            })}
                        </>
                    )}
                </div>
            )}

            {editor && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30">
                    <div className="bg-white w-full max-w-5xl max-h-[90vh] rounded shadow relative flex flex-col">
                        <div className="flex-shrink-0 p-6 border-b border-gray-200">
                            <button
                                className="absolute top-2 right-2 p-1 rounded hover:bg-gray-100"
                                onClick={() => setEditor(null)}
                            >
                                <XMarkIcon className="h-6 w-6 text-gray-500" />
                            </button>
                            <h2 className="text-xl font-bold">
                                {editor.isNew ? "New File" : isManager ? "Edit File" : "View File"}
                            </h2>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium mb-1">Path</label>
                                <input
                                    type="text"
                                    value={editor.path}
                                    onChange={(e) => setEditor({ ...editor, path: e.target.value })}
                                    disabled={!editor.isNew || !isManager}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 font-mono text-sm"
                                    placeholder="e.g. CLAUDE.md or tools/foo.md"
                                />
                                {editor.isNew && (
                                    <p className="mt-1 text-xs text-gray-500">
                                        Use forward slashes for folders (e.g. <code>tools/scaffold.md</code>).
                                    </p>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium mb-1">Content</label>
                                <textarea
                                    value={editor.content}
                                    onChange={(e) => setEditor({ ...editor, content: e.target.value })}
                                    disabled={!isManager}
                                    rows={22}
                                    className="w-full border rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm disabled:bg-gray-50"
                                />
                            </div>
                        </div>
                        <div className="flex-shrink-0 flex justify-end gap-2 p-6 border-t border-gray-200">
                            <button
                                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded"
                                onClick={() => setEditor(null)}
                            >
                                {isManager ? "Cancel" : "Close"}
                            </button>
                            {isManager && (
                                <button
                                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded"
                                    onClick={saveFile}
                                    disabled={loading}
                                >
                                    {loading ? "Saving..." : "Save"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
